import React, { useEffect } from 'react'
import {
  Container,
  Divider,
  Grid,
  Tab,
  Tabs,
  Toolbar,
  Typography,
} from '@material-ui/core'
import { Link, useHistory, useParams } from 'react-router-dom'
import { useReactiveVar } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet-async'
import { setContext } from '@sentry/react'
import {
  ProjectVersionFragment,
  useProjectQuery,
} from '../../generated/graphql'
import { Sidebar } from '../layout/Sidebar'
import {
  activeVersionVar,
  calculatorVar,
  comparisonVersionCalculatorVar,
  comparisonVersionVar,
  dictionariesVar,
  projectVar,
} from '../../apollo/reactive-vars'
import { SystemContainerSidebarContent } from '../system-container/sidebar/SystemContainerSidebarContent'
import { ProjectVersions } from '../project-versions/ProjectVersions'
import { Loader } from '../common/Loader'
import { versionResultCategories } from '../../provider/result-categories'
import { useComparisonCalculator } from '../../calculators/use-comparison-calculator'
import { useDefaultCalculator } from '../../calculators/use-default-calculator'
import { useLayoutStyles } from '../layout/use-layout-styles'
import {
  createDictionaries,
  emptyDictionaries,
} from '../common/util/dictionary-util'

type Params = {
  id: string
}

type Props = {
  children: React.ReactElement
}

export function ProjectLayout({ children }: Props): React.ReactElement {
  const classes = useLayoutStyles()
  const { t } = useTranslation()
  const { id } = useParams<Params>()
  const { data } = useProjectQuery({ variables: { id } })
  const project = useReactiveVar(projectVar)
  const activeVersion = useReactiveVar(activeVersionVar)
  const calculator = useReactiveVar(calculatorVar)
  const comparisonVersion = useReactiveVar(comparisonVersionVar)
  const comparisonCalculator = useReactiveVar(comparisonVersionCalculatorVar)
  const { updateDefaultCalculation } = useDefaultCalculator()
  const { updateComparisonCalculation } = useComparisonCalculator()
  const history = useHistory()

  const cleanUp = () => {
    projectVar(null)
    activeVersionVar(null)
    comparisonVersionVar(null)
    comparisonVersionCalculatorVar(null)
    dictionariesVar(emptyDictionaries)

    setContext('project', null)
    setContext('activeVersion', null)
  }

  useEffect(
    () =>
      // Cleanup on component unmount
      () => {
        cleanUp()
      },

    [],
  )

  useEffect(() => {
    if (!data || !data.project) {
      cleanUp()
      return
    }
    setContext('project', {
      id: data.project?.id,
      createdAt: data.project?.createdAt,
    })

    projectVar(data.project)
  }, [data])

  useEffect(() => {
    const currentProject = projectVar()

    if (!currentProject) {
      setContext('project', null)
      setContext('activeVersion', null)
      return
    }

    const currentVersion = activeVersionVar()

    const alreadyActiveVersion = currentVersion
      ? currentProject.versions.find((v) => v.id === currentVersion.id)
      : null
    const defaultVersion = currentProject.versions.find((v) => v.isDefault)
    const newActiveVersion = alreadyActiveVersion ?? defaultVersion

    setContext('activeVersion', {
      id: newActiveVersion?.id,
    })

    activeVersionVar(newActiveVersion)
  }, [project])

  useEffect(() => {
    updateDefaultCalculation()
    if (comparisonVersion) {
      updateComparisonCalculation()
    }
    // Only trigger this when the temperature hour series is updated (other cases handled elsewhere)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.temperatureHourSeries])

  // Cypress can't render/interact with WebGL -> use this method as a bridge for clicking resources.
  useEffect(() => {
    if (window.Cypress) {
      window.cypressActiveVersion = () => activeVersion
    }
  }, [activeVersion])

  useEffect(() => {
    updateDefaultCalculation()

    // Adding updateDefaultCalculation to the deps causes an infinite render loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeVersion])

  useEffect(() => {
    updateComparisonCalculation()

    // Adding updateComparisonCalculation to the deps causes an infinite render loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparisonVersion])

  useEffect(() => {
    if (project?.versions) {
      const dictionaries = createDictionaries(project.versions, t)
      dictionariesVar(dictionaries)
    }
  }, [project?.versions, t])

  if (!project || !activeVersion) {
    return <Loader />
  }

  const pathPrefix = `/projects/${project.id}`
  const tabs = [
    {
      id: 'project-tab',
      title: t('project.tabs.project'),
      path: pathPrefix,
    },
    {
      id: 'hour-series-tab',
      title: t('project.tabs.hourSeries'),
      path: `${pathPrefix}/hour-series`,
    },
    {
      id: 'energy-model-tab',
      title: t('project.tabs.energyModels'),
      path: `${pathPrefix}/energymodels`,
    },
    {
      id: 'settings-tab',
      title: t('project.tabs.settings'),
      path: `${pathPrefix}/settings`,
    },
    {
      id: 'report-tab',
      title: t('project.tabs.report'),
      path: `${pathPrefix}/report`,
    },
  ]

  const activeTabIndex = tabs.findIndex((tab) =>
    history.location.pathname.endsWith(tab.path),
  )

  const onVersionSelected = (version: ProjectVersionFragment) => {
    activeVersionVar(version)

    setContext('activeVersion', {
      id: version?.id,
    })

    comparisonVersionVar(null)
    comparisonVersionCalculatorVar(null)
  }

  const allSystemContainers = calculator?.getAllContainers() ?? []
  const comparisonSystemContainers = comparisonCalculator?.getAllContainers()

  const energyBus = calculator?.findEnergyBus()
  const comparisonEnergyBus = comparisonCalculator?.findEnergyBus()

  return (
    <>
      <Helmet title={project.name} />

      <Sidebar isExtended={comparisonSystemContainers !== undefined}>
        {/* Allocate space for app bar. */}
        <Toolbar />

        <SystemContainerSidebarContent
          containers={allSystemContainers}
          comparisonContainers={comparisonSystemContainers}
          energyBus={energyBus}
          comparisonEnergyBus={comparisonEnergyBus}
          resultCategories={versionResultCategories}
        />
      </Sidebar>

      <main className={classes.content}>
        <Container>
          <Grid container>
            <Grid item sm={8} zeroMinWidth>
              <Typography
                className={classes.title}
                variant="h3"
                title={project.name}
              >
                {project.name}
              </Typography>
              <Typography className={classes.projectInfo} variant="subtitle1">
                {t('project.projectId')}: {project.projectId}
              </Typography>
            </Grid>
            <Grid item sm={4}>
              <ProjectVersions
                project={project}
                activeVersion={activeVersion}
                onVersionSelected={onVersionSelected}
              />
            </Grid>
          </Grid>

          <Tabs value={activeTabIndex}>
            {tabs.map((tab) => (
              <Tab
                key={tab.id}
                id={tab.id}
                label={tab.title}
                component={Link}
                to={tab.path}
              />
            ))}
          </Tabs>
        </Container>

        <Divider />

        {children}
      </main>
    </>
  )
}
